import { Subject } from 'rxjs';
import { orderService } from './service';
import { isActiveOrder } from '../../shared/orderStatus';

const FIELDS_TO_UPDATE_ORDER_DEFAULT = {
  address_id: '',
  delivery_type: 'delivery',
  pay_type_id: '',
  date: '',
  fio: '',
  comment: '',
  promo_code_text: '',
  promo_sum: '',
  time_from: '',
  time_to: '',
  time_gap: null,
  store_id: '',
  bonus_sum: 0,
  card_id: '',
  additional_fields_collected: [],
};

export class OrderStore {
  constructor(rootStore) {
    this.rootStore = rootStore;
    this.subject = new Subject();
    this.order = {};
    this.orders = [];
    this.selectedAddressDistrictId = '';
    this.orderDatePeriod = {};
    this.minDateForOrder = '';
    this.linkForPay = '';
    this.fieldsToUpdateOrder = Object.assign({}, FIELDS_TO_UPDATE_ORDER_DEFAULT);
    this.orderMade = false;
    this.cantDeliverOnDate = { date: '', can_date: '', message: '' };
    // Этап подписания акта аренды тары: акт и его статус, пришедшие с сервера.
    // rentActStatus: '' | 'not_required' | 'unsigned' | 'signed' | 'stale'
    this.rentAct = null;
    this.rentActStatus = '';
    // Состояние загрузки списка заказов: его читают сразу два экрана ЛК
    // (главная и #orders), поэтому оно живёт в сторе, а не в компоненте.
    this.ordersLoading = false;
    this.ordersLoaded = false;
    this.ordersError = null;
    this._ordersPromise = null;
  }

  _emit() { this.subject.next(this); }

  setOrder(o) { this.order = o || {}; this.orderMade = false; this._emit(); }
  setOrders(list) { this.orders = list || []; this._emit(); }
  setLinkForPay(v) { this.linkForPay = v || ''; this._emit(); }
  setMinDateForOrder(v) { this.minDateForOrder = v || ''; this._emit(); }

  setFieldsToUpdateOrder(patch) {
    this.fieldsToUpdateOrder = Object.assign({}, this.fieldsToUpdateOrder, patch);
    this._emit();
  }

  resetFieldsToUpdateOrder() {
    this.fieldsToUpdateOrder = Object.assign({}, FIELDS_TO_UPDATE_ORDER_DEFAULT);
    this._emit();
  }

  setSelectedAddressDistrictId(id) {
    this.selectedAddressDistrictId = id || '';
    this._emit();
  }

  setOrderDatePeriod(value) {
    this.orderDatePeriod = value || {};
    // Бэкенд (District.complete_order_date) кладёт выбранный период в `period`
    // (= periods.first, если period_id не передан), как и мобилка. Берём его
    // time_from/time_to, чтобы период (в т.ч. единственный) сразу был выбран.
    if (value && value.period && value.period.period) {
      this.setFieldsToUpdateOrder({
        time_from: value.period.period[0],
        time_to: value.period.period[1],
      });
    }
  }

  async convertCartToOrder() {
    this.order = {};
    this.fieldsToUpdateOrder = Object.assign({}, FIELDS_TO_UPDATE_ORDER_DEFAULT);
    this.orderDatePeriod = {};
    this.minDateForOrder = '';
    this.selectedAddressDistrictId = '';
    this.linkForPay = '';
    this.cantDeliverOnDate = { date: '', can_date: '', message: '' };
    this.orderMade = false;
    this.rentAct = null;
    this.rentActStatus = '';
    const res = await orderService.convertCartToOrder();
    this.setOrder(res.order);
    return res;
  }

  async updateTimePeriods(districtId, date, periodId) {
    const res = await orderService.updateTimePeriods(districtId, date, periodId);
    this.setOrderDatePeriod(res);
    this.setFieldsToUpdateOrder({ date: res.date });
    return res;
  }

  async updateMinDateForOrder(districtId, date, periodId) {
    const res = await this.updateTimePeriods(districtId, date, periodId);
    if (res) {
      this.setMinDateForOrder(res.date);
      this.setFieldsToUpdateOrder({
        date: res.date,
        time_from: res.period && res.period.period && res.period.period[0],
        time_to: res.period && res.period.period && res.period.period[1],
      });
    }
    return res;
  }

  async updateCommonParams(params) {
    if (!this.order || !Object.keys(this.order).length) return;
    const fields = params || this.fieldsToUpdateOrder;
    const res = await orderService.updateCommonParams(fields);
    if (res.order) this.setOrder(res.order);
    return res;
  }

  async applyPromoCode() {
    const res = await orderService.applyPromoCode(this.fieldsToUpdateOrder);
    if (res.order) this.setOrder(res.order);
    return res;
  }

  async updateDate(date) {
    const res = await orderService.updateTimePeriods(this.selectedAddressDistrictId, date);
    if (res.date_in_request && res.date_in_request.status === 'ok') {
      this.resetDateErrors();
      this.setOrderDatePeriod(res);
      this.setFieldsToUpdateOrder({ date: res.date });
    } else {
      // res.date — это «следующая доступная» дата, которую нашёл сервер
      // (см. District.complete_order_date: цикл вперёд до 31 дня).
      // res.date_in_request.date — наоборот, запрошенная и недоступная.
      // В cantDeliverOnDate.can_date кладём предложение сервера.
      this.cantDeliverOnDate = {
        date,
        can_date: res.date,
        message: res.date_in_request && res.date_in_request.message,
      };
      this._emit();
    }
    return res;
  }

  resetDateErrors() {
    this.cantDeliverOnDate = { date: '', can_date: '', message: '' };
    this._emit();
  }

  async makeOrder() {
    const res = await orderService.makeOrder();
    this.orderMade = true;
    this._emit();
    this.rootStore.authStore.bootstrap();
    // Сбрасываем поля только при успехе: при ошибке пользователь остаётся на
    // шаге оплаты, и его выбор (адрес, дата, способ оплаты…) должен уцелеть.
    this.resetFieldsToUpdateOrder();
    return res.message;
  }

  // extra — доп. параметры оплаты, напр. { save_card: false } из галки согласия
  async payOrder(oneCardPayment, extra) {
    const res = oneCardPayment
      ? await orderService.payOrderJustPay()
      : await orderService.payOrder(extra);
    if (res.redirect) this.setLinkForPay(res.redirect);
    if (res.status === 'ok') {
      this.orderMade = true;
      this._emit();
      this.rootStore.authStore.bootstrap();
      this.resetFieldsToUpdateOrder();
    }
    return res;
  }

  // Требуется ли клиенту пройти шаг подписи прямо сейчас
  get rentActSignRequired() {
    return this.rentActStatus === 'unsigned' || this.rentActStatus === 'stale';
  }

  _applyRentAct(res) {
    this.rentActStatus = (res && res.status) || '';
    this.rentAct = (res && res.rent_act) || null;
    this._emit();
    return res;
  }

  async prepareRentAct() {
    return this._applyRentAct(await orderService.prepareRentAct());
  }

  async signRentAct(code) {
    if (!this.rentAct) return null;
    return this._applyRentAct(await orderService.signRentAct(this.rentAct.id, code));
  }

  async resendRentActCode() {
    if (!this.rentAct) return null;
    return orderService.resendRentActCode(this.rentAct.id);
  }

  async skipRentAct() {
    if (!this.rentAct) return null;
    const res = await orderService.skipRentAct(this.rentAct.id);
    if (res && res.status === 'skipped') {
      this.rentActStatus = 'skipped';
      this._emit();
    }
    return res;
  }

  // Гейт на make/pay отдаёт 400 с телом; обёртка api кладёт его в err.body
  // (err.message для 400 равен буквально 'error', показывать его нельзя)
  applyRentActGateError(err) {
    const body = err && err.body;
    if (!body || !body.errors || !body.errors.rent_act_sign_required) return false;
    this.rentActStatus = 'unsigned';
    this._emit();
    return true;
  }

  async getOrders() {
    const res = await orderService.getOrders();
    this.ordersLoaded = true;
    this.ordersError = null;
    this.setOrders(res.orders); // setOrders сам делает _emit()
    return res.orders;
  }

  // Загрузка списка «по требованию» с дедупликацией: главная ЛК и экран
  // #orders монтируются в одной сессии страницы и не должны сделать два
  // одинаковых GET v3/orders/list.
  async ensureOrders() {
    if (this.ordersLoaded) return this.orders;
    if (this._ordersPromise) return this._ordersPromise;
    this.ordersLoading = true;
    this.ordersError = null;
    this._emit();
    this._ordersPromise = this.getOrders().then(
      (orders) => {
        this.ordersLoading = false;
        this._ordersPromise = null;
        this._emit();
        return orders;
      },
      (err) => {
        this.ordersLoading = false;
        this._ordersPromise = null;
        this.ordersError = (err && err.message) || 'Ошибка';
        this._emit();
        throw err;
      }
    );
    return this._ordersPromise;
  }

  get activeOrders() {
    return (this.orders || []).filter(isActiveOrder);
  }

  get pastOrders() {
    return (this.orders || []).filter((o) => !isActiveOrder(o));
  }

  async submitReview(payload) {
    const res = await orderService.submitReview(payload);
    if (res.status === 'ok') await this.rootStore.authStore.bootstrap();
    return res;
  }

  async cancelOrder(orderId) {
    const res = await orderService.cancelOrder(orderId);
    if (res.order) this.setOrder(res.order);
    await this.rootStore.authStore.bootstrap();
    return res;
  }

  // Смена способа оплаты у уже оформленного заказа. Список заказов
  // перечитываем целиком: сервер пересчитывает и pay_type_change тоже.
  async changePayType(orderId, payTypeId) {
    await orderService.changePayType(orderId, payTypeId);
    return this.getOrders();
  }

  // Онлайн-оплата уже оформленного заказа. Ответ той же формы, что у чекаута:
  // {redirect} — уводим на страницу эквайера/QR, иначе оплата прошла сразу.
  async payExistingOrder(orderId) {
    const res = await orderService.payExistingOrder(orderId);
    if (!res || !res.redirect) await this.getOrders();
    return res;
  }
}



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/stores/orderStore/index.js